import React, { createContext, useState, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User, Achievement } from '../types/index';
import { getRankFromPoints, checkAchievements } from '../utils/rankUtils';
import { mockAuthService } from '../services/mockAuthService';
import { authServiceClient as supabaseAuthService } from '../services/authService';
import { authServiceClient as secureAuthService } from '../services/secureAuthService';

// Check if we should use secure authentication
const USE_SECURE_AUTH = import.meta.env.VITE_USE_SECURE_AUTH === 'true' || 
                        import.meta.env.NODE_ENV === 'production';

// Check if Supabase is properly configured
const USE_SUPABASE = !USE_SECURE_AUTH && import.meta.env.VITE_SUPABASE_URL && 
                     import.meta.env.VITE_SUPABASE_URL !== 'your_supabase_project_url' &&
                     import.meta.env.VITE_SUPABASE_URL !== 'https://placeholder.supabase.co';

// Use secure auth if enabled, otherwise Supabase if configured, otherwise mock
const authServiceClient = USE_SECURE_AUTH ? secureAuthService :
                         USE_SUPABASE ? supabaseAuthService : 
                         mockAuthService;


interface AuthContextType {
  user: User | null;
  login: (rollNo: string, password: string) => Promise<void>;
  signup: (userData: SignupData) => Promise<void>;
  logout: () => void;
  updatePoints: (points: number, activityType?: 'before-after' | 'video', location?: string) => void;
  checkDailyLogin: () => void;
  unlockAchievement: (achievement: Achievement) => void;
  clearLastAchievement: () => void;
  updateUser: (updates: Partial<User>) => void;
  isLoading: boolean;
  isInitialLoading: boolean;
  lastUnlockedAchievement: Achievement | null;
}

interface SignupData {
  rollNo: string;
  name: string;
  department: string;
  email: string;
  password: string;
  selfie?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [lastUnlockedAchievement, setLastUnlockedAchievement] = useState<Achievement | null>(null);
  
  // Load user from Supabase on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await authServiceClient.getCurrentUser();
        if (userData) {
          // Check for achievements that should be unlocked based on current stats
          const possibleAchievements = checkAchievements(userData);
          const newAchievements = possibleAchievements.filter(
            newAch => !userData.achievements.find(existing => existing.id === newAch.id)
          );
          
          if (newAchievements.length > 0) {
            const updatedUser = {
              ...userData,
              achievements: [...userData.achievements, ...newAchievements],
              points: userData.points + (newAchievements.length * 10)
            };
            
            setUser(updatedUser);
            
            // Save to database in background
            authServiceClient.updateProfile(userData.id, updatedUser).catch(() => {});
            
            // Show notification for first achievement
            setLastUnlockedAchievement(newAchievements[0]);
          } else {
            setUser(userData);
          }
        }
      } catch (error) {
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadUser();

    // TODO: Add Supabase auth listener when configured
    // if (USE_SUPABASE) {
    //   const { data: { subscription } } = supabase.auth.onAuthStateChange(...)
    //   return () => subscription.unsubscribe();
    // }
  }, []);

  const login = async (emailOrRollNo: string, password: string) => {
    setIsLoading(true);
    try {
      await authServiceClient.login(emailOrRollNo, password);
      const userData = await authServiceClient.getCurrentUser();
      if (userData) {
        // Check for achievements that should be unlocked based on current stats
        const possibleAchievements = checkAchievements(userData);
        const newAchievements = possibleAchievements.filter(
          newAch => !userData.achievements.find(existing => existing.id === newAch.id)
        );
        
        if (newAchievements.length > 0) {
          const updatedUser = {
            ...userData,
            achievements: [...userData.achievements, ...newAchievements],
            points: userData.points + (newAchievements.length * 10)
          };
          
          setUser(updatedUser);
          
          // Save to database in background
          authServiceClient.updateProfile(userData.id, updatedUser).catch(err => {
          });
          
          // Show notification for first achievement
          setLastUnlockedAchievement(newAchievements[0]);
        } else {
          setUser(userData);
        }
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (userData: SignupData) => {
    setIsLoading(true);
    try {
      await authServiceClient.signup(userData);
      // Don't log the user in after signup since they need approval
      // Just sign them out to ensure clean state
      await authServiceClient.logout();
      setUser(null);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authServiceClient.logout();
      setUser(null);
    } catch (error) {
    }
  };

  const updatePoints = async (points: number, activityType?: 'before-after' | 'video', location?: string) => {
    if (user) {
      const today = new Date();
      const lastActivity = user.lastActivityDate ? new Date(user.lastActivityDate) : null;
      
      let newStreak = user.streak;
      
      // Check if this is a new day activity
      if (!lastActivity || !isSameDay(lastActivity, today)) {
        if (lastActivity && isYesterday(lastActivity, today)) {
          // Continue streak
          newStreak = user.streak + 1;
        } else if (!lastActivity || daysBetween(lastActivity, today) > 1) {
          // Break streak
          newStreak = 1;
        }
      }
      
      const newPoints = user.points + points;
      const newRank = getRankFromPoints(newPoints);
      
      // Update daily activity count
      const lastDailyDate = user.lastDailyActivityDate ? new Date(user.lastDailyActivityDate) : null;
      const dailyCount = lastDailyDate && isSameDay(lastDailyDate, today) 
        ? (user.dailyActivityCount || 0) + 1 
        : 1;
      
      // Update consecutive activity days
      let consecutiveDays = user.consecutiveActivityDays || 0;
      const lastConsecutiveDate = user.lastConsecutiveActivityDate ? new Date(user.lastConsecutiveActivityDate) : null;
      
      if (!lastConsecutiveDate || isYesterday(lastConsecutiveDate, today)) {
        consecutiveDays = lastConsecutiveDate ? consecutiveDays + 1 : 1;
      } else if (!isSameDay(lastConsecutiveDate, today)) {
        consecutiveDays = 1;
      }
      
      // Update unique locations
      const uniqueLocations = [...(user.uniqueLocations || [])];
      if (location && !uniqueLocations.includes(location)) {
        uniqueLocations.push(location);
      }
      
      const updatedUser = { 
        ...user, 
        points: newPoints,
        rank: newRank,
        streak: newStreak,
        lastActivityDate: today,
        totalActivities: user.totalActivities + 1,
        beforeAfterCount: user.beforeAfterCount + (activityType === 'before-after' ? 1 : 0),
        videoCount: user.videoCount + (activityType === 'video' ? 1 : 0),
        dailyActivityCount: dailyCount,
        lastDailyActivityDate: today,
        consecutiveActivityDays: consecutiveDays,
        lastConsecutiveActivityDate: today,
        uniqueLocations
      };
      
      // Check for new achievements - filter out already unlocked ones
      const allPossibleAchievements = checkAchievements(updatedUser, { type: 'activity' });
      
      const newAchievements = allPossibleAchievements.filter(
        newAch => !user.achievements.find(existing => existing.id === newAch.id)
      );
      
      if (newAchievements.length > 0) {
        updatedUser.achievements = [...updatedUser.achievements, ...newAchievements];
        // Award bonus points for achievements
        updatedUser.points += newAchievements.length * 10;
        // Set the last unlocked achievement
        setLastUnlockedAchievement(newAchievements[newAchievements.length - 1]);
      }
      
      setUser(updatedUser);
      
      // Update in database
      try {
        await authServiceClient.updateProfile(user.id, updatedUser);
      } catch (error) {
      }
    }
  };
  
  // Helper functions for date comparison
  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };
  
  const isYesterday = (date1: Date, date2: Date) => {
    const yesterday = new Date(date2);
    yesterday.setDate(yesterday.getDate() - 1);
    return isSameDay(date1, yesterday);
  };
  
  const daysBetween = (date1: Date, date2: Date) => {
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.round(Math.abs((date2.getTime() - date1.getTime()) / oneDay));
  };

  const checkDailyLogin = async () => {
    if (user) {
      const today = new Date();
      const lastLogin = user.lastLoginDate ? new Date(user.lastLoginDate) : null;
      
      if (!lastLogin || !isSameDay(lastLogin, today)) {
        let newLoginStreak = user.loginStreak;
        let bonusPoints = 0; // Daily login bonus
        
        if (lastLogin && isYesterday(lastLogin, today)) {
          newLoginStreak = user.loginStreak + 1;
        } else if (!lastLogin || daysBetween(lastLogin, today) > 1) {
          newLoginStreak = 1;
        }
        
        // Only update if streak actually changed
        if (newLoginStreak !== user.loginStreak) {
          const updatedUser = {
            ...user,
            lastLoginDate: today,
            loginStreak: newLoginStreak,
            points: user.points + bonusPoints,
            rank: getRankFromPoints(user.points + bonusPoints),
            achievements: [...user.achievements] // Keep existing achievements
          };
          
          // Check for NEW login achievements only
          const newAchievements = checkAchievements(updatedUser).filter(
            newAch => !user.achievements.find(existing => existing.id === newAch.id)
          );
          
          if (newAchievements.length > 0) {
            updatedUser.achievements = [...updatedUser.achievements, ...newAchievements];
            updatedUser.points += newAchievements.length * 10;
            // Set the last unlocked achievement
            setLastUnlockedAchievement(newAchievements[newAchievements.length - 1]);
          }
          
          setUser(updatedUser);
          
          // Update in database
          try {
            await authServiceClient.updateProfile(user.id, updatedUser);
          } catch (error) {
          }
        } else {
          // Just update the last login date without triggering achievements
          const updatedUser = { ...user, lastLoginDate: today };
          setUser(updatedUser);
          
          try {
            await authServiceClient.updateProfile(user.id, { lastLoginDate: today });
          } catch (error) {
          }
        }
      }
    }
  };

  const unlockAchievement = async (achievement: Achievement) => {
    if (user && !user.achievements.find(a => a.id === achievement.id)) {
      const updatedUser = {
        ...user,
        achievements: [...user.achievements, achievement],
        points: user.points + 10
      };
      setUser(updatedUser);
      
      // Update in database
      try {
        await authServiceClient.updateProfile(user.id, updatedUser);
      } catch (error) {
      }
      
      setLastUnlockedAchievement(achievement);
    }
  };

  const clearLastAchievement = () => {
    setLastUnlockedAchievement(null);
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates });
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, updatePoints, checkDailyLogin, unlockAchievement, clearLastAchievement, updateUser, isLoading, isInitialLoading, lastUnlockedAchievement }}>
      {children}
    </AuthContext.Provider>
  );
};